import React, { useMemo } from 'react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Button, Group, Text, ActionIcon, Tooltip } from '@mantine/core';
import { IconPlus, IconFileDownload, IconCloudUpload, IconRefresh } from '@tabler/icons-react';
import Tabla from '../../../globales/componentes/tabla/tabla';
import { obtenerColumnasComisiones, obtenerSemaforoYRango, type ComisionesTableMeta } from './constantesTablaComisiones';
import type { IConfiguracionComisiones } from '../interfaces';

interface TablaComisionesVistaProps {
  datos: IConfiguracionComisiones[];
  alDescargarPlantilla?: () => void;
  alCargaMasiva?: () => void;
  /** Vuelve a consultar la matriz (descarta una edición en curso). */
  alRecargar?: () => void;
  actualizarFila: (id: string, campo: keyof IConfiguracionComisiones, valor: string | number) => void;
  eliminarFila: (id: string) => void;
  guardarFila?: (id: string) => void;
  filaEnEdicion?: string | null;
  iniciarEdicion?: (id: string) => void;
  cancelarEdicion?: () => void;
  
  agregarFila: () => void;
  mostrarNotificacionObligatorio?: () => void;
  intentoGuardar?: boolean;
}

export const TablaComisionesVista: React.FC<TablaComisionesVistaProps> = ({
  datos,
  alDescargarPlantilla,
  alCargaMasiva,
  alRecargar,
  actualizarFila,
  eliminarFila,
  guardarFila,
  filaEnEdicion,
  iniciarEdicion,
  cancelarEdicion,
  agregarFila,
  mostrarNotificacionObligatorio,
  intentoGuardar
}) => {
  const columnas = useMemo(() => obtenerColumnasComisiones(), []);

  const meta: ComisionesTableMeta = {
    actualizarFila,
    eliminarFila,
    guardarFila,
    filaEnEdicion,
    iniciarEdicion,
    cancelarEdicion,
    mostrarNotificacionObligatorio,
    intentoGuardar
  };

  const tabla = useReactTable({
    data: datos,
    columns: columnas,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.Id,
    meta
  });

  const getRowStyle = (row: any) => {
    const margen = row.original.Margen || 0;
    const { color } = obtenerSemaforoYRango(margen);
    let borderColor = 'transparent';
    if (color === 'green') borderColor = '#2b8a3e';
    if (color === 'red') borderColor = '#e03131';
    if (color === 'yellow') borderColor = '#f59f00';
    
    return {
      borderLeft: `4px solid ${borderColor}`
    };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Group justify="space-between" mb="sm">
        <Text fw={600} size="sm" c="dimmed">
          Tabla de Margen y Comisiones
        </Text>
        <Group gap="sm">
          {/* Íconos con el estilo estándar de las tablas de los demás módulos (Tareas, Equipos, Clientes). */}
          <Group gap={4}>
            {alRecargar && (
              <Tooltip zIndex={100000} label="Refrescar" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
                <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alRecargar}>
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip zIndex={100000} label="Descargar Plantilla" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
              <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alDescargarPlantilla} disabled={!alDescargarPlantilla}>
                <IconFileDownload size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip zIndex={100000} label="Carga Masiva" style={{ fontSize: '10px', padding: '3px 5px' }} withinPortal openDelay={100}>
              <ActionIcon variant="subtle" color="primary" radius="xl" onClick={alCargaMasiva} disabled={!alCargaMasiva}>
                <IconCloudUpload size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Button color="primary" variant="light" size="xs" radius="lg" leftSection={<IconPlus color="var(--mantine-color-primary-0)" size={12} />} onClick={agregarFila} disabled={filaEnEdicion !== null && filaEnEdicion !== undefined}><Text fw={500} size="xs">Agregar Fila</Text></Button>
        </Group>
      </Group>

      <div style={{ flex: 1, minHeight: 400 }}>
        <Tabla tabla={tabla} getRowStyle={getRowStyle} modoEdicion={true} filaEnEdicion={filaEnEdicion} />
      </div>
    </div>
  );
};
